import { useEffect, useState } from "react";
import { FaSave } from "react-icons/fa";
import { FaChevronLeft, FaChevronRight, FaTrash } from "react-icons/fa6";
import { LuTable, LuX } from "react-icons/lu";
import apiClient from "../../../api-services/ApiClient";
import Preloader from "../../../app-components/preloader/Preloader";
import {
  SegmentInterface,
  SegmentQuestionInterface,
} from "../../../interface-and-types/inspection.schema";
import { confirmAlert } from "../../../helpers/SweetAlert";
import {
  notifyError,
  notifySuccess,
} from "../../../app-components/toast/toast";

const ManageSegmentForm = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [segments, setSegments] = useState<SegmentInterface[]>([]); // # store all segments && render to table
  const [segmentName, setSegmentName] = useState<string>(""); // # retrieve name of segment from input for new segment creation;
  const [newSegment, setNewSegment] = useState<Partial<SegmentInterface>>({
    segmentQuestions: [],
  });

  const [isAddNewQuestionsModalOpen, setIsAddNewQuestionsModalOpen] =
    useState(false);
  const [isAddNewSegmentModalOpen, setIsAddNewSegmentModalOpen] =
    useState(false);

  const [currentSegment, setCurrentSegment] = useState<number>(0);
  const [sectionDropdown, setSectionDropdown] = useState<number>(0);

  // const [currentNewSegment, setCurrentNewSegment] = useState<number>(0);
  // const [originalSegment, setOriginalSegment] = useState<SegmentInterface[]>(
  //   []
  // );
  // const [segmentNew, setSegmentNew] = useState<SegmentInterface[]>([]);
  // const [newSegmentNew, setNewSegmentNew] = useState<SegmentInterface[]>([]);

  
  useEffect(() => {
    getAllSegments();
  }, []);

  /**
   *  # getAllSegments
   */
  const getAllSegments = async () => {
    setLoading(true);

    try {
      const res: any = await apiClient.get("Segment");
      const sortDataBySegmentNumber = res.data.data.sort(
        (a: any, b: any) => a.segmentNumber - b.segmentNumber
      );

      setSegments(sortDataBySegmentNumber);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   *  # Create new segment
   */
  const createNewSegment = async (e: any) => {
    e.preventDefault();

    const data = {
      name: segmentName,
      segmentNumber: segments.length + 1, //main segment lenght + 1
      segmentQuestions: newSegment.segmentQuestions?.map((item, index) => ({
        question: item.question,
        questionNumber: index + 1,
        questionType: item.questionType,
        id: index + 1,
        name: segmentName,
      })),
    };

    try {
      setLoading(true);
      await apiClient.post(`Segment`, data);
      notifySuccess("successful!");

      //! optimistic delete - remove from array without refreshing the server;
      getAllSegments();
    } catch (error: any) {
      notifyError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   *  # add new question to existing segment
   */
  const addNewQuestionsToExistingSegment = async (e: any) => {
    e.preventDefault();
    const segmentId = segments[currentSegment]?.id; // retrieve the current segment ID;
    const questionNumber = segments[currentSegment]?.segmentQuestions?.length;
    const name = segments[currentSegment]?.name;

    const data: any = {
      segmentQuestions: newSegment?.segmentQuestions?.map((item, index) => ({
        question: item.question,
        questionNumber: questionNumber + index + 1,
        questionType: item.questionType,
        id: questionNumber + index + 1,
        name: name,
      })),
    };

    try {
      setLoading(true);
      await apiClient.post(`Segment/${segmentId}/SegmentQuestion`, data);
      notifySuccess("Successful!");

      //! optimistic delete - remove from array without refreshing the server;
      getAllSegments();
      setNewSegment({
        segmentQuestions: [],
      });
    } catch (error: any) {
      notifyError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   *  # updating question in existing segment
   */
  // const updateQuestionsInExistingSegmentDUB = async (e: any) => {
  //   e.preventDefault();
  //   const currentSegmentData = segments[currentSegment];
  //   const segmentId = currentSegmentData?.id; // retrieve the current segment ID

  //   const data: any = {
  //     segmentQuestions: currentSegmentData.segmentQuestions,
  //   };

  //   try {
  //     setLoading(true);
  //     await apiClient.patch(`Segment/${segmentId}/SegmentQuestion`, data);
  //     notifySuccess("successful!");
  //   } catch (error: any) {
  //     notifyError(JSON.stringify(error));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const updateQuestionsInExistingSegment = async (e: any) => {
    e.preventDefault();
    const currentSegmentData = segments[currentSegment];
    const segmentId = currentSegmentData?.id; // retrieve the current segment ID

    const data: any = {
      name: currentSegmentData.name,
      segmentNumber: sectionDropdown,
      segmentQuestions: currentSegmentData.segmentQuestions,
    };

    try {
      setLoading(true);
      await apiClient.put(`Segment/${segmentId}`, data);
      await getAllSegments();
      notifySuccess("successful!");
    } catch (error: any) {
      notifyError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   *  # delete a segment
   */
  const deleteSegmentById = async (e: any) => {
    e.preventDefault();

    const confirmResponse = await confirmAlert(
      "This action cannot be undone! Do you want to delete this segment?"
    );

    if (!confirmResponse.isConfirmed) return;

    const segmentId = segments[currentSegment]?.id; // retrieve current segment ID;

    try {
      setLoading(true);
      await apiClient.delete(`Segment/${segmentId}`);
      //! optimistic delete - remove from array without refreshing the server;
      getAllSegments();
    } catch (error: any) {
      notifyError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   *  # delete a segment question by question ID
   */
  const deleteSegmentQuestionById = async (segmentQuestionId: string) => {
    const confirmResponse = await confirmAlert(
      "<small>This action cannot be undone!<br /> Do you want to delete this question?</small>"
    );
    if (!confirmResponse.isConfirmed) return;

    try {
      setLoading(true);
      const segmentId = segments[currentSegment]?.id;

      await apiClient.delete(
        `Segment/${segmentId}/SegmentQuestion/${segmentQuestionId}`
      );

      //! optimistic delete - remove from array without refreshing the server;
      getAllSegments();
      notifySuccess("Successful!");
    } catch (error: any) {
      notifyError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  // # navigate to previous page
  const prevPage = () => {
    setCurrentSegment(currentSegment - 1);
    setSectionDropdown(currentSegment - 1);
  };

  // # navigate to next page
  const nextPage = () => {
    setCurrentSegment(currentSegment + 1);
    setSectionDropdown(currentSegment + 1);
  };

  // # navigate to next page
  const handleSectionDropdown = (val: any) => {
    setSectionDropdown(val);
  };

  // update segment questons input
  const handleUpdateSegmentInputChange = <
    T extends keyof SegmentQuestionInterface
  >(
    index: number,
    field: T,
    value: SegmentQuestionInterface[T]
  ) => {
    const segmentsCopy = [...segments];
    const currentSegmentData = segmentsCopy[currentSegment];

    if (!currentSegmentData) {
      console.error("Current segment not found.");
      notifyError("info not accurate");
      return;
    }

    // Ensure segmentQuestions is an array and make a copy of it
    const currentSegmentQuestions = [
      ...(currentSegmentData.segmentQuestions || []),
    ];

    // Check if the specified question exists in the array
    const selectedRowInCurrentSegment = currentSegmentQuestions[index];
    if (!selectedRowInCurrentSegment) {
      console.error(`Question at index ${index} not found in segment.`);
      notifyError("info not accurate");
      return;
    }

    // Update the specific field in the question
    const updatedRow = { ...selectedRowInCurrentSegment, [field]: value };

    // Replace the old question row with the updated one
    currentSegmentQuestions[index] = updatedRow;

    // Update the entire segments
    segmentsCopy[currentSegment] = {
      ...currentSegmentData, // Preserve other properties
      segmentQuestions: currentSegmentQuestions,
    };

    // Update the state
    setSegments(segmentsCopy);
  };

  // values from newly added questions
  const handleNewSegmentInputChange = <
    T extends keyof SegmentQuestionInterface
  >(
    index: number,
    field: T,
    value: SegmentQuestionInterface[T]
  ) => {
    const segmentsCopy = { ...newSegment };

    // Get the current segment and ensure it exists
    const currentSegmentInfo = segmentsCopy;
    if (!currentSegmentInfo) {
      console.error("Current segment not found.");
      notifyError("info not accurate");
      return;
    }

    // Ensure segmentQuestions is an array and make a copy of it
    const currentSegmentQuestions = [
      ...(currentSegmentInfo.segmentQuestions || []),
    ];

    // Check if the specified question exists in the array
    const currentQuestionInSegment = currentSegmentQuestions[index];
    if (!currentQuestionInSegment) {
      console.error(`Question at index ${index} not found in segment.`);
      notifyError("info not accurate");
      return;
    }

    // Update the specific field in the question
    const updatedQuestion = { ...currentQuestionInSegment, [field]: value };

    // Replace the old question with the updated one
    currentSegmentQuestions[index] = updatedQuestion;

    // Update the state
    setNewSegment({
      ...currentSegmentInfo,
      segmentQuestions: currentSegmentQuestions,
    });
  };

  // # create a new role
  const handleAddRow = () => {
    const data: any = { ...newSegment };
    const currentSegmentQuestions = [...(data?.segmentQuestions || [])];

    const segment_question = {
      name: "",
      question: "",
      questionNumber: 0,
      questionType: "Text",
      id: "",
      createdAt: "",
      updatedAt: "",
      modifiedAt: "",
      createdBy: "",
      answer: "",
      comment: null,
      priority: null,
    };

    currentSegmentQuestions.push(segment_question);

    setNewSegment({ ...data, segmentQuestions: currentSegmentQuestions });
  };

  const handleSegmentName = (e: any, current: any) => {
    const updated_segments = segments.map((item: any, i: number) =>
      i === current ? { ...item, name: e.target.value } : item
    );
    setSegments(updated_segments);
  };

  return (
    <>
      {/* page lead */}
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-4 pb-24 flex justify-between items-center">
          <h4 className="lg:text-[16px] text-white nunito font-medium">
            Manage Inspection Questions
          </h4>

          {/*page actions */}
          <div className="flex gap-1">
            <button
              className="disabled:bg-gray-500 text-[10px] flex gap-2 items-center justify-start bg-red-700 shadow p-2 rounded text-white font-medium px-4"
              type="button"
              onClick={prevPage}
              disabled={currentSegment === 0}
            >
              <FaChevronLeft className="text-md" />
              <span>Back</span>
            </button>

            <button
              className="disabled:bg-gray-500 text-[10px] flex gap-2 items-center bg-blue-700 shadow p-2 rounded text-white font-medium px-4"
              type="button"
              onClick={nextPage}
              disabled={segments.length - 1 === currentSegment}
            >
              <span>Next</span>
              <FaChevronRight className="text-md" />
            </button>

            <button
              className="flex gap-1 text-[10px] items-center bg-green-700 shadow p-2 rounded text-white font-medium px-4"
              type="button"
              onClick={updateQuestionsInExistingSegment}
            >
              <FaSave />
              <span>Update</span>
            </button>

            <button
              className="flex gap-1 text-[10px] items-center bg-blue-700 shadow p-2 rounded text-white font-medium px-4"
              type="button"
              onClick={() => setIsAddNewSegmentModalOpen(true)}
            >
              <LuTable />
              <span>Add New Segment</span>
            </button>

            <button
              className="flex gap-1 text-[10px] items-center bg-red-700 shadow p-2 rounded text-white font-medium px-4"
              type="button"
              onClick={deleteSegmentById}
            >
              <FaTrash />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-90px]">
        {/* main form questions */}
        <div className="rounded-lg bg-white border border-gray-200 mb-5">
          <div className="p-4">
            <div className="flex gap-3 justify-between text-slate-800 mb-4 font-medium">
              <div className="text-md w-3/4 flex gap-1">
                <h1 className="text-md w-full">
                  <input
                    value={segments[currentSegment]?.name}
                    onChange={(e) => handleSegmentName(e, currentSegment)}
                    className="border px-2 p-1 rounded w-full"
                  />
                </h1>

                <select
                  id="array-length-dropdown"
                  className="text-xs border px-2 p-1 "
                  value={sectionDropdown}
                  onChange={(e) =>
                    handleSectionDropdown(Number(e.target.value))
                  }
                >
                  {Array.from({ length: segments.length }, (_, index) => (
                    <option key={index} value={index}>
                      Change to Section {index + 1}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-1/4 text-end">
                <span className="bg-gray-100 rounded-full px-2 text-sm">
                  {currentSegment + 1} of {segments?.length}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200 text-left text-xs">
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 text-center">
                      SN
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      {segments[currentSegment]?.name}
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {segments[currentSegment]?.segmentQuestions.map(
                    (row, index) => (
                      <tr key={index} className="even:bg-gray-50 text-xs">
                        <td className="border border-gray-300 px-2 py-1 text-center">
                          {index + 1}
                        </td>

                        <td className="border border-gray-300 px-2 py-1">
                          {
                            <textarea
                              className="w-full border border-1 p-2"
                              value={row.question}
                              onChange={(e) =>
                                handleUpdateSegmentInputChange(
                                  index,
                                  "question",
                                  e.target.value
                                )
                              }
                              placeholder="Enter Question"
                            ></textarea>
                          }
                        </td>

                        <td className="border border-gray-300 px-2 py-1 w-[100px]">
                          <button
                            onClick={() => {
                              deleteSegmentQuestionById(row?.id);
                            }}
                            className="text-red-600 flex gap-2 font-medium"
                          >
                            <FaTrash /> Delete
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3">
              <button
                className="mt-4 text-[10px] flex gap-2 items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-600"
                onClick={() => setIsAddNewQuestionsModalOpen(true)}
              >
                <span>Add New Question</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* add new questions */}
      {isAddNewQuestionsModalOpen && (
        <section className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center">
          <button
            onClick={() => setIsAddNewQuestionsModalOpen(false)}
            className="mt-4 text-red-600 hover:underline p-3 rounded-full bg-white mb-4 text-xl border border-gray-500 shadow animate-modalShow"
          >
            <LuX />
          </button>

          <div className="bg-white p-12 rounded shadow-md w-full max-w-xl animate-modalShow">
            <form
              onSubmit={addNewQuestionsToExistingSegment}
              autoComplete="off"
            >
              <h2 className="text-md font-medium mb-4 flex-col border-b">
                Add Questions
              </h2>

              <div className=" max-h-[55vh] overflow-y-scroll">
                <div className="overflow-x-auto">
                  <table className="table-auto w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200 text-left text-xs">
                        <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                          {segments[currentSegment]?.name}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {newSegment?.segmentQuestions?.map((row, index) => (
                        <tr key={index} className="even:bg-gray-50 text-xs">
                          <td className="border border-gray-300 px-2 py-1">
                            {
                              <textarea
                                className="w-full border border-1 p-2"
                                value={row.question}
                                onChange={(e) =>
                                  handleNewSegmentInputChange(
                                    index,
                                    "question",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Question"
                              ></textarea>
                            }
                          </td>
                        </tr>
                      ))}
                      <tr className="even:bg-gray-50 text-xs">
                        <td className="border border-gray-300 px-2 py-1">
                          <button
                            type="button"
                            className="flex font-normal justify-center text-xs bg-blue-600 text-white py-2 rounded hover:bg-red-600 px-3 items-center gap-2"
                            onClick={handleAddRow}
                          >
                            <span>Add new row</span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <button
                type="submit"
                className="w-full mx-auto flex font-normal justify-center mt-12 text-sm bg-red-700 text-white py-2 rounded hover:bg-red-600"
              >
                Create Question
              </button>
            </form>
          </div>
        </section>
      )}

      {/* add new questions */}
      {isAddNewSegmentModalOpen && (
        <section className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center">
          <button
            onClick={() => setIsAddNewSegmentModalOpen(false)}
            className="mt-4 text-red-600 hover:underline p-3 rounded-full bg-white mb-4 text-xl border border-gray-500 shadow animate-modalShow"
          >
            <LuX />
          </button>

          <div className="bg-white p-12 rounded shadow-md w-full max-w-xl animate-modalShow">
            <form onSubmit={createNewSegment} autoComplete="off">
              <h2 className="text-md font-medium mb-4 flex-col border-b">
                Add New Segment
              </h2>

              <div className=" max-h-[55vh] overflow-y-scroll">
                <div className="overflow-x-auto">
                  <table className="table-auto w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200 text-left text-xs">
                        <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                          <textarea
                            className="w-full border border-1 p-2"
                            value={segmentName}
                            onChange={(e) => setSegmentName(e.target.value)}
                            placeholder="Enter Section Name"
                          ></textarea>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {newSegment?.segmentQuestions?.map((row, index) => (
                        <tr key={index} className="even:bg-gray-50 text-xs">
                          <td className="border border-gray-300 px-2 py-1">
                            {
                              <textarea
                                className="w-full border border-1 p-2"
                                value={row.question}
                                onChange={(e) =>
                                  handleNewSegmentInputChange(
                                    index,
                                    "question",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Question"
                              ></textarea>
                            }
                          </td>
                        </tr>
                      ))}
                      <tr className="even:bg-gray-50 text-xs">
                        <td className="border border-gray-300 px-2 py-1">
                          <button
                            type="button"
                            className="flex font-normal justify-center text-xs bg-blue-600 text-white py-2 rounded hover:bg-red-600 px-3 items-center gap-2"
                            onClick={handleAddRow}
                          >
                            <span>Add new row</span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <button
                type="submit"
                className="w-full mx-auto flex font-normal justify-center mt-12 text-sm bg-red-700 text-white py-2 rounded hover:bg-red-600"
              >
                Create Segment
              </button>
            </form>
          </div>
        </section>
      )}

      <Preloader loading={loading} />
    </>
  );
};

export default ManageSegmentForm;
